import { useState } from "react"
import {LayoutGrid, ArrowRight} from 'lucide-react'
import { useCalculator } from "@/context/CalculatorContext"

type PanShape = "rectangle" | "round"

interface PanDims {
  shape: PanShape
  length: number
  width: number
  diameter: number
}

function calcArea(dims: PanDims): number {
  if (dims.shape === "rectangle") {
    return Math.max(0, dims.length * dims.width)
  }
  const r = dims.diameter / 2
  return Math.PI * r * r
}

const DEFAULT_ORIG: PanDims = { shape: "rectangle", length: 20, width: 20, diameter: 20 }
const DEFAULT_TARGET: PanDims = { shape: "round", length: 23, width: 23, diameter: 23 }

export default function PanConverterCard() {
  const { applyPanConversion } = useCalculator()
  const [orig, setOrig] = useState<PanDims>(DEFAULT_ORIG)
  const [target, setTarget] = useState<PanDims>(DEFAULT_TARGET)

  const origArea = calcArea(orig)
  const targetArea = calcArea(target)
  const factor = origArea > 0 ? targetArea / origArea : 0

  const updateOrig = (key: keyof PanDims, value: string | PanShape) => {
    setOrig((prev) => ({
      ...prev,
      [key]: key === "shape" ? value : Math.max(0, parseFloat(value as string) || 0),
    }))
  }

  const updateTarget = (key: keyof PanDims, value: string | PanShape) => {
    setTarget((prev) => ({
      ...prev,
      [key]: key === "shape" ? value : Math.max(0, parseFloat(value as string) || 0),
    }))
  }

  const inputClass =
    "w-full tabular-nums text-right pr-8 pl-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent transition-shadow"

  const renderDimInputs = (
    dims: PanDims,
    update: (k: keyof PanDims, v: string | PanShape) => void,
    prefix: string
  ) => {
    if (dims.shape === "rectangle") {
      return (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div className="relative">
            <input
              type="number"
              min="1"
              value={dims.length}
              onChange={(e) => update("length", e.target.value)}
              placeholder="Length"
              className={inputClass}
              id={`${prefix}-length`}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">cm</span>
            <label htmlFor={`${prefix}-length`} className="block text-xs text-gray-400 mt-0.5 text-center">
              Length
            </label>
          </div>
          <div className="relative">
            <input
              type="number"
              min="1"
              value={dims.width}
              onChange={(e) => update("width", e.target.value)}
              placeholder="Width"
              className={inputClass}
              id={`${prefix}-width`}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">cm</span>
            <label htmlFor={`${prefix}-width`} className="block text-xs text-gray-400 mt-0.5 text-center">
              Width
            </label>
          </div>
        </div>
      )
    }
    return (
      <div className="relative mt-2">
        <input
          type="number"
          min="1"
          value={dims.diameter}
          onChange={(e) => update("diameter", e.target.value)}
          placeholder="Diameter"
          className={inputClass}
          id={`${prefix}-diameter`}
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">cm</span>
        <label htmlFor={`${prefix}-diameter`} className="block text-xs text-gray-400 mt-0.5 text-center">
          Diameter
        </label>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 card-hover">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
          <LayoutGrid size={17} className="text-emerald-600" strokeWidth={2.2} />
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-900 leading-tight">Convert Baking Pan Sizes</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Scale recipe by geometric area ratio between pans
          </p>
        </div>
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] gap-3 mb-4 items-start">
        {/* Original Pan */}
        <div>
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Original Pan</p>
          <select
            value={orig.shape}
            onChange={(e) => updateOrig("shape", e.target.value as PanShape)}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
          >
            <option value="rectangle">Square / Rectangle</option>
            <option value="round">Round</option>
          </select>
          {renderDimInputs(orig, updateOrig, "orig")}
          <p className="text-xs text-gray-400 mt-2 tabular-nums text-right">
            Area: {origArea.toFixed(1)} cm²
          </p>
        </div>

        <div className="pt-8">
          <ArrowRight size={16} className="text-gray-300" />
        </div>

        {/* Target Pan */}
        <div>
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Target Pan</p>
          <select
            value={target.shape}
            onChange={(e) => updateTarget("shape", e.target.value as PanShape)}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
          >
            <option value="rectangle">Square / Rectangle</option>
            <option value="round">Round</option>
          </select>
          {renderDimInputs(target, updateTarget, "target")}
          <p className="text-xs text-gray-400 mt-2 tabular-nums text-right">
            Area: {targetArea.toFixed(1)} cm²
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2.5 mb-4 border border-gray-100">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Area Scale Factor</span>
        <span className="text-sm font-bold text-indigo-700 tabular-nums">
          {factor > 0 ? factor.toFixed(4) : "—"}×
        </span>
      </div>

      <button
        onClick={() => applyPanConversion(factor)}
        disabled={factor <= 0}
        className="w-full bg-indigo-600 text-white text-sm font-semibold py-2.5 px-4 rounded-lg hover:bg-indigo-700 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm hover:shadow"
      >
        Apply Pan Conversion
      </button>
    </div>
  )
}
